import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCommentDots, faPaperPlane, faQuoteLeft, faCheckCircle, faStar } from "@fortawesome/free-solid-svg-icons";
import { API_BASE_URL } from "../config/api";

const Feedback = (props) => {
  const [feedback, setFeedback] = useState("");
  const courseId = props.courseid;
  const [feedbacks, setFeedbacks] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (courseId) {
      fetch(`${API_BASE_URL}/api/feedbacks/${courseId}`)
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) {
            setFeedbacks(data); // Load all feedbacks, display controls scrolling
          }
        })
        .catch((error) => console.error("Error fetching feedback:", error));
    }
  }, [courseId]);

  const sendFeedback = () => {
    if (!feedback.trim()) {
      alert("Please enter feedback before submitting.");
      return;
    }

    fetch(`${API_BASE_URL}/api/feedbacks`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ comment: feedback.trim(), course_id: courseId })
    })
      .then(() => {
        const newItem = { comment: feedback.trim() };
        setFeedbacks((prev) => [newItem, ...prev]);
        setFeedback("");
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 3000);
      })
      .catch((error) => console.error("Error submitting feedback:", error));
  };

  // First 3 always visible without scroll, rest scrollable
  const visibleFeedbacks = feedbacks.slice(0, 3);
  const extraFeedbacks = feedbacks.slice(3);

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "20px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.03)",
        overflow: "hidden"
      }}
    >
      {/* Section Header */}
      <div
        style={{
          padding: "20px 28px",
          borderBottom: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          background: "#fafbff"
        }}
      >
        <FontAwesomeIcon icon={faCommentDots} style={{ color: "#4f46e5", fontSize: "1.2rem" }} />
        <h3 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 800, color: "#0f172a", fontFamily: "'Outfit', sans-serif" }}>
          Course Feedback & Reviews
        </h3>
        {feedbacks.length > 0 && (
          <span
            style={{
              marginLeft: "auto",
              background: "#e0e7ff",
              color: "#4f46e5",
              borderRadius: "20px",
              padding: "3px 12px",
              fontSize: "0.8rem",
              fontWeight: 700
            }}
          >
            {feedbacks.length} {feedbacks.length === 1 ? "review" : "reviews"}
          </span>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "0" }}>

        {/* ── Left: Submit Feedback ── */}
        <div style={{ padding: "28px", borderRight: "1px solid #f1f5f9" }}>
          <p style={{ color: "#64748b", fontSize: "0.88rem", marginBottom: "16px", lineHeight: "1.6" }}>
            Share your thoughts or suggestions to help us continuously improve this module.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <textarea
              rows="4"
              placeholder="Write your feedback comment here..."
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: "12px",
                border: "1.5px solid #e2e8f0",
                fontSize: "0.92rem",
                color: "#0f172a",
                outline: "none",
                resize: "vertical",
                fontFamily: "inherit",
                background: "#fafbff",
                transition: "border-color 0.2s ease",
                boxSizing: "border-box"
              }}
              onFocus={(e) => (e.target.style.borderColor = "#4f46e5")}
              onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
            />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              {submitted ? (
                <span style={{ color: "#10b981", fontSize: "0.85rem", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
                  <FontAwesomeIcon icon={faCheckCircle} /> Feedback submitted!
                </span>
              ) : <div />}

              <button
                onClick={sendFeedback}
                style={{
                  background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)",
                  color: "#fff",
                  border: "none",
                  padding: "11px 24px",
                  borderRadius: "10px",
                  fontWeight: 700,
                  fontSize: "0.88rem",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(79,70,229,0.25)",
                  transition: "all 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "7px"
                }}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                Submit Feedback
              </button>
            </div>
          </div>
        </div>

        {/* ── Right: Reviews (3 visible + scrollable for rest) ── */}
        <div style={{ padding: "28px" }}>
          <h4 style={{ margin: "0 0 14px", fontSize: "1rem", fontWeight: 700, color: "#0f172a" }}>
            Student Reviews
          </h4>

          {feedbacks.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 20px",
                background: "#f8fafc",
                borderRadius: "12px",
                border: "1.5px dashed #e2e8f0"
              }}
            >
              <FontAwesomeIcon icon={faStar} style={{ color: "#cbd5e1", fontSize: "2rem", marginBottom: "10px" }} />
              <p style={{ color: "#94a3b8", fontSize: "0.88rem", margin: 0 }}>
                No feedback yet. Be the first to share your experience!
              </p>
            </div>
          ) : (
            <div>
              {/* First 3 always shown */}
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {visibleFeedbacks.map((item, idx) => (
                  <FeedbackCard key={idx} item={item} />
                ))}
              </div>

              {/* Extra feedbacks in a scrollable area */}
              {extraFeedbacks.length > 0 && (
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    marginTop: "10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    paddingRight: "4px",
                    // Custom scrollbar styling
                    scrollbarWidth: "thin",
                    scrollbarColor: "#cbd5e1 transparent"
                  }}
                >
                  {extraFeedbacks.map((item, idx) => (
                    <FeedbackCard key={`extra-${idx}`} item={item} />
                  ))}
                </div>
              )}

              {extraFeedbacks.length > 0 && (
                <p style={{ fontSize: "0.78rem", color: "#94a3b8", marginTop: "8px", textAlign: "right" }}>
                  ↑ Scroll to see {extraFeedbacks.length} more {extraFeedbacks.length === 1 ? "review" : "reviews"}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Extracted feedback card for reuse
const FeedbackCard = ({ item }) => (
  <div
    style={{
      background: "#f8fafc",
      borderRadius: "10px",
      padding: "12px 16px",
      border: "1px solid #f1f5f9",
      display: "flex",
      gap: "10px",
      alignItems: "flex-start"
    }}
  >
    <FontAwesomeIcon icon={faQuoteLeft} style={{ color: "#818cf8", fontSize: "0.85rem", marginTop: "3px", flexShrink: 0 }} />
    <p style={{ margin: 0, color: "#334155", fontSize: "0.88rem", lineHeight: "1.55" }}>
      {item.comment}
    </p>
  </div>
);

export default Feedback;
